// frontend/src/components/Dashboard/AnalyticsCards.jsx - COMPLETELY CLEANED
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCurrency } from "../../context/CurrencyContext";
import {
  faChartLine,
  faUsers,
  faShoppingCart,
  faDollarSign,
  faPercentage,
  faEye,
  faClock,
  faArrowUp,
  faArrowDown,
  faTruck,
  faBox,
  faStar,
  faTractor,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
  faSpinner,
  faRocket,
  faBoxes,
  faWallet,
  faRepeat,
  faCalendarAlt,
  faWeightScale,
  faShip,
  faClipboardList,
  faExclamationTriangle,
  faCheckDouble,
  faGlobe,
  faAnchor,
  faAward,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";

// ============================================
// HELPERS
// ============================================
const getStatusColor = (status) => {
  const colors = {
    pending: "#f59e0b",
    processing: "#3b82f6",
    confirmed: "#8b5cf6",
    shipped: "#8b5cf6",
    out_for_delivery: "#f59e0b",
    delivered: "#16a34a",
    cancelled: "#dc2626",
    refunded: "#6b7280",
    ordered: "#D9A321",
    in_transit: "#f59e0b",
    customs: "#ef4444",
    delayed: "#dc2626",
    completed: "#16a34a",
    failed: "#dc2626",
    paid: "#16a34a",
    unpaid: "#dc2626",
    processing_payment: "#f59e0b",
    failed_payment: "#dc2626",
    refunded_payment: "#6b7280",
    deposit_received: "#8b5cf6",
    ready_for_pickup: "#10b981",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusIcon = (status) => {
  const icons = {
    pending: faHourglassHalf,
    processing: faSpinner,
    confirmed: faCheckCircle,
    shipped: faRocket,
    out_for_delivery: faTruck,
    delivered: faCheckDouble,
    cancelled: faTimesCircle,
    refunded: faWallet,
    ordered: faClock,
    in_transit: faTruck,
    customs: faExclamationTriangle,
    delayed: faExclamationTriangle,
    completed: faCheckCircle,
    failed: faTimesCircle,
    paid: faDollarSign,
    unpaid: faTimesCircle,
    processing_payment: faSpinner,
    failed_payment: faTimesCircle,
    refunded_payment: faWallet,
    deposit_received: faDollarSign,
    ready_for_pickup: faCheckCircle,
  };
  return icons[status] || faBox;
};

// ============================================
// MAIN COMPONENT
// ============================================
const AnalyticsCards = ({ analytics, userRole, userType, isLoading }) => {
  const { formatAmount } = useCurrency();
  const isAdmin = userRole === "admin" || userRole === "super_admin";

  if (isLoading) {
    return (
      <div className="analytics-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading analytics...</p>
      </div>
    );
  }

  const safeAnalytics = {
    pendingOrders: analytics?.orderStatusBreakdown?.pending || 0,
    processingOrders: analytics?.orderStatusBreakdown?.processing || 0,
    shippedOrders: analytics?.orderStatusBreakdown?.shipped || 0,
    deliveredOrders: analytics?.orderStatusBreakdown?.delivered || 0,
    cancelledOrders: analytics?.orderStatusBreakdown?.cancelled || 0,
    totalCustomers: analytics?.uniqueCustomers || 0,
    totalFarmers: analytics?.bulkMetrics?.activeFarmers || 0,
    totalRevenue: analytics?.totalRevenue || 0,
    totalOrders: analytics?.totalOrders || 0,
    bulkOrders: analytics?.bulkMetrics?.totalBulkQuantity || 0,
    avgOrderValue: analytics?.avgOrderValue || 0,
    conversionRate: analytics?.conversionRate || 0,
    clv: analytics?.clv || 0,
    repeatRate: analytics?.repeatRate || 0,
    cartAbandonment: analytics?.cartAbandonment || 0,
    revenueChange: analytics?.revenueChange || "+0%",
    ordersChange: analytics?.ordersChange || "+0%",
    bulkOrdersChange: analytics?.bulkOrdersChange || "+0%",
    avgOrderChange: analytics?.avgOrderChange || "+$0",
    conversionChange: analytics?.conversionChange || "+0%",
    clvChange: analytics?.clvChange || "+$0",
    repeatChange: analytics?.repeatChange || "+0%",
    abandonmentChange: analytics?.abandonmentChange || "+0%",
    bulkMetrics: analytics?.bulkMetrics || {
      totalBulkRevenue: 0,
      totalBulkQuantity: 0,
      avgFarmerOrderValue: 0,
      activeFarmers: 0,
    },
    recentActivity: analytics?.recentActivity || [],
    globalOrders: analytics?.globalOrders || 0,
    globalRevenue: analytics?.globalRevenue || 0,
  };

  // Order Status Cards Data
  const orderStatusCards = [
    {
      title: "Pending Orders",
      value: safeAnalytics.pendingOrders,
      icon: faHourglassHalf,
      color: "#f59e0b",
      bgColor: "#fef3c7",
      description: "Awaiting processing",
    },
    {
      title: "Processing",
      value: safeAnalytics.processingOrders,
      icon: faSpinner,
      color: "#3b82f6",
      bgColor: "#dbeafe",
      description: "Being prepared",
    },
    {
      title: "Shipped",
      value: safeAnalytics.shippedOrders,
      icon: faRocket,
      color: "#8b5cf6",
      bgColor: "#ede9fe",
      description: "In transit",
    },
    {
      title: "Delivered",
      value: safeAnalytics.deliveredOrders,
      icon: faCheckCircle,
      color: "#16a34a",
      bgColor: "#dcfce7",
      description: "Successfully completed",
    },
    {
      title: "Cancelled",
      value: safeAnalytics.cancelledOrders,
      icon: faTimesCircle,
      color: "#dc2626",
      bgColor: "#fee2e2",
      description: "Order cancelled",
    },
  ];

  // Customer Types Data
  const customerTypes = [
    {
      label: "Total Customers",
      value: safeAnalytics.totalCustomers?.toLocaleString() || "0",
      icon: faUsers,
      color: "#3b82f6",
      bgColor: "#dbeafe",
      description: "All registered users",
    },
    {
      label: "Active Farmers",
      value: safeAnalytics.totalFarmers?.toLocaleString() || "0",
      icon: faTractor,
      color: "#10b981",
      bgColor: "#dcfce7",
      description: "Registered farmers",
    },
    {
      label: "Bulk Orders",
      value: safeAnalytics.bulkOrders?.toLocaleString() || "0",
      icon: faBoxes,
      color: "#8b5cf6",
      bgColor: "#ede9fe",
      description: "100kg+ orders",
    },
    {
      label: "Total Orders",
      value: safeAnalytics.totalOrders?.toLocaleString() || "0",
      icon: faShoppingCart,
      color: "#f59e0b",
      bgColor: "#fef3c7",
      description: "All-time orders",
    },
  ];

  // Main Metrics Data
  const mainCards = [
    {
      title: "Total Revenue",
      value: formatAmount(safeAnalytics.totalRevenue, true),
      change: safeAnalytics.revenueChange,
      trend: safeAnalytics.revenueChange?.startsWith("+") ? "up" : "down",
      icon: faDollarSign,
      color: "#f59e0b",
      description: "Total sales revenue",
    },
    {
      title: "Total Orders",
      value: safeAnalytics.totalOrders.toLocaleString(),
      change: safeAnalytics.ordersChange,
      trend: safeAnalytics.ordersChange?.startsWith("+") ? "up" : "down",
      icon: faShoppingCart,
      color: "#ef4444",
      description: "All-time orders",
    },
    {
      title: "Bulk Orders",
      value: safeAnalytics.bulkOrders.toLocaleString(),
      change: safeAnalytics.bulkOrdersChange,
      trend: safeAnalytics.bulkOrdersChange?.startsWith("+") ? "up" : "down",
      icon: faBoxes,
      color: "#8b5cf6",
      description: "100kg+ orders",
    },
    {
      title: "Avg Order Value",
      value: formatAmount(safeAnalytics.avgOrderValue, true),
      change: safeAnalytics.avgOrderChange,
      trend: safeAnalytics.avgOrderChange?.startsWith("+") ? "up" : "down",
      icon: faChartLine,
      color: "#10b981",
      description: "Average order amount",
    },
  ];

  // Performance Metrics Data
  const performanceCards = [
    {
      title: "Conversion Rate",
      value: `${safeAnalytics.conversionRate}%`,
      change: safeAnalytics.conversionChange,
      trend: safeAnalytics.conversionChange?.startsWith("+") ? "up" : "down",
      icon: faPercentage,
      color: "#10b981",
      description: "Visitors to customers",
    },
    {
      title: "Customer LTV",
      value: formatAmount(safeAnalytics.clv, true),
      change: safeAnalytics.clvChange,
      trend: safeAnalytics.clvChange?.startsWith("+") ? "up" : "down",
      icon: faStar,
      color: "#8b5cf6",
      description: "Lifetime value per customer",
    },
    {
      title: "Repeat Purchase Rate",
      value: `${safeAnalytics.repeatRate}%`,
      change: safeAnalytics.repeatChange,
      trend: safeAnalytics.repeatChange?.startsWith("+") ? "up" : "down",
      icon: faRepeat,
      color: "#ec4899",
      description: "Customers who return",
    },
    {
      title: "Cart Abandonment",
      value: `${safeAnalytics.cartAbandonment}%`,
      change: safeAnalytics.abandonmentChange,
      trend: safeAnalytics.abandonmentChange?.startsWith("-") ? "down" : "up",
      icon: faEye,
      color: "#ef4444",
      description: "Abandoned carts rate",
    },
  ];

  // ============================================
  // SUB-COMPONENTS
  // ============================================

  const OrderStatusSection = () => (
    <div className="analytics-order-status">
      <div className="analytics-section-header">
        <div className="analytics-section-title">
          <FontAwesomeIcon icon={faClipboardList} />
          <h3>Order Status Overview</h3>
        </div>
        <span className="analytics-badge">
          <FontAwesomeIcon icon={faGlobe} /> Global Sourcing
        </span>
      </div>
      <div className="analytics-status-grid">
        {orderStatusCards.map((card, index) => (
          <div
            key={index}
            className="analytics-status-card"
            style={{
              background: card.bgColor,
              borderLeftColor: card.color,
            }}
          >
            <div
              className="analytics-status-icon"
              style={{ color: card.color }}
            >
              <FontAwesomeIcon icon={card.icon} />
            </div>
            <div className="analytics-status-info">
              <div className="analytics-status-value">{card.value}</div>
              <div className="analytics-status-title">{card.title}</div>
              <div className="analytics-status-description">
                {card.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const CustomerSummarySection = () => (
    <div className="analytics-customer-summary">
      <div className="analytics-section-header">
        <div className="analytics-section-title">
          <FontAwesomeIcon icon={faUsers} />
          <h3>Account Overview</h3>
        </div>
        <span className="analytics-badge">
          <FontAwesomeIcon icon={faAward} /> Premium Service
        </span>
      </div>
      <div className="analytics-customer-grid">
        {customerTypes.map((type, index) => (
          <div
            key={index}
            className="analytics-customer-card"
            style={{ background: type.bgColor }}
          >
            <FontAwesomeIcon
              icon={type.icon}
              style={{ color: type.color, fontSize: "1.5rem" }}
            />
            <div className="analytics-customer-value">{type.value}</div>
            <div className="analytics-customer-label">{type.label}</div>
            <div className="analytics-customer-description">
              {type.description}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const BulkMetricsSection = () => {
    const { bulkMetrics } = safeAnalytics;
    const metrics = [
      {
        label: "Bulk Revenue",
        value: formatAmount(bulkMetrics.totalBulkRevenue || 0, true),
        icon: faDollarSign,
        color: "#f59e0b",
      },
      {
        label: "Bulk Quantity Sold",
        value: `${bulkMetrics.totalBulkQuantity?.toLocaleString() || "0"} kg`,
        icon: faWeightScale,
        color: "#10b981",
      },
      {
        label: "Avg Farmer Order",
        value: formatAmount(bulkMetrics.avgFarmerOrderValue || 0, true),
        icon: faTractor,
        color: "#8b5cf6",
      },
      {
        label: "Active Farmers",
        value: bulkMetrics.activeFarmers?.toLocaleString() || "0",
        icon: faUsers,
        color: "#3b82f6",
      },
    ];

    return (
      <div className="analytics-bulk-metrics">
        <div className="analytics-section-header">
          <div className="analytics-section-title">
            <FontAwesomeIcon icon={faTractor} />
            <h3>Bulk & Farmer Metrics</h3>
          </div>
          <span className="analytics-badge">
            <FontAwesomeIcon icon={faAnchor} /> Global Logistics
          </span>
        </div>
        <div className="analytics-bulk-grid">
          {metrics.map((metric, index) => (
            <div key={index} className="analytics-bulk-card">
              <div
                className="analytics-bulk-icon"
                style={{ color: metric.color }}
              >
                <FontAwesomeIcon icon={metric.icon} />
              </div>
              <div className="analytics-bulk-value">{metric.value}</div>
              <div className="analytics-bulk-label">{metric.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const GlobalStatsSection = () => (
    <div className="analytics-global">
      <div className="analytics-section-header">
        <div className="analytics-section-title">
          <FontAwesomeIcon icon={faShip} />
          <h3>Global Supply Chain</h3>
        </div>
        <span className="analytics-badge">
          <FontAwesomeIcon icon={faGlobe} /> Worldwide Sourcing
        </span>
      </div>
      <div className="analytics-global-grid">
        <div className="analytics-global-card">
          <div className="analytics-global-value">
            {safeAnalytics.globalOrders || 0}
          </div>
          <div className="analytics-global-label">
            Orders from Global Sources
          </div>
        </div>
        <div className="analytics-global-card gold">
          <div className="analytics-global-value gold">
            {formatAmount(safeAnalytics.globalRevenue || 0, true)}
          </div>
          <div className="analytics-global-label">Global Revenue</div>
        </div>
        <div className="analytics-global-card">
          <div className="analytics-global-value">
            <FontAwesomeIcon icon={faAward} />
          </div>
          <div className="analytics-global-label">Premium Quality</div>
        </div>
        <div className="analytics-global-card">
          <div className="analytics-global-value">
            <FontAwesomeIcon icon={faHandshake} />
          </div>
          <div className="analytics-global-label">Trusted Partners</div>
        </div>
      </div>
    </div>
  );

  const RecentActivitySection = () => {
    const activities = safeAnalytics.recentActivity || [];

    if (activities.length === 0) {
      return (
        <div className="analytics-recent-activity">
          <div className="analytics-section-header">
            <div className="analytics-section-title">
              <FontAwesomeIcon icon={faClock} />
              <h3>Recent Order Activity</h3>
            </div>
          </div>
          <div className="analytics-activity-empty">
            <FontAwesomeIcon icon={faBox} size="2x" />
            <p>No recent activity to display</p>
          </div>
        </div>
      );
    }

    return (
      <div className="analytics-recent-activity">
        <div className="analytics-section-header">
          <div className="analytics-section-title">
            <FontAwesomeIcon icon={faClock} />
            <h3>Recent Order Activity</h3>
          </div>
          <span className="analytics-badge">
            <FontAwesomeIcon icon={faCalendarAlt} /> Last 7 days
          </span>
        </div>
        <div className="analytics-activity-timeline">
          {activities.slice(0, 10).map((activity, index) => (
            <div key={index} className="analytics-activity-item">
              <div
                className="analytics-activity-icon"
                style={{ color: getStatusColor(activity.status) }}
              >
                <FontAwesomeIcon icon={getStatusIcon(activity.status)} />
              </div>
              <div className="analytics-activity-details">
                <div className="analytics-activity-title">
                  Order #{activity.orderNumber || activity._id?.slice(-8)}
                </div>
                <div
                  className="analytics-activity-status"
                  style={{ color: getStatusColor(activity.status) }}
                >
                  {activity.status?.charAt(0).toUpperCase() +
                    activity.status?.slice(1) || "Unknown"}
                </div>
                <div className="analytics-activity-date">
                  {activity.createdAt
                    ? new Date(activity.createdAt).toLocaleDateString()
                    : "N/A"}
                  {activity.createdAt && (
                    <span className="analytics-activity-time">
                      {new Date(activity.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  )}
                </div>
              </div>
              <div className="analytics-activity-amount">
                {formatAmount(activity.total?.usd || 0, true)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER METRICS CARDS
  // ============================================
  const renderMetricsCards = () => (
    <>
      <div className="analytics-metrics-grid">
        {mainCards.map((card, index) => (
          <div key={index} className="analytics-metric-card">
            <div className="analytics-metric-header">
              <div
                className="analytics-metric-icon"
                style={{ background: `${card.color}15` }}
              >
                <FontAwesomeIcon
                  icon={card.icon}
                  style={{ color: card.color }}
                />
              </div>
              <span className={`analytics-metric-trend ${card.trend}`}>
                <FontAwesomeIcon
                  icon={card.trend === "up" ? faArrowUp : faArrowDown}
                />
                {card.change}
              </span>
            </div>
            <div className="analytics-metric-value">{card.value}</div>
            <div className="analytics-metric-title">{card.title}</div>
            <div className="analytics-metric-description">
              {card.description}
            </div>
          </div>
        ))}
      </div>

      <div className="analytics-metrics-grid performance">
        {performanceCards.map((card, index) => (
          <div key={index} className="analytics-metric-card performance">
            <div className="analytics-metric-header">
              <div
                className="analytics-metric-icon"
                style={{ background: `${card.color}15` }}
              >
                <FontAwesomeIcon
                  icon={card.icon}
                  style={{ color: card.color }}
                />
              </div>
              <span className={`analytics-metric-trend ${card.trend}`}>
                <FontAwesomeIcon
                  icon={card.trend === "up" ? faArrowUp : faArrowDown}
                />
                {card.change}
              </span>
            </div>
            <div className="analytics-metric-value">{card.value}</div>
            <div className="analytics-metric-title">{card.title}</div>
            <div className="analytics-metric-description">
              {card.description}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="analytics-container">
      <OrderStatusSection />
      <CustomerSummarySection />

      {isAdmin && (
        <>
          {renderMetricsCards()}
          <BulkMetricsSection />
          <GlobalStatsSection />
        </>
      )}

      <RecentActivitySection />
    </div>
  );
};

export default AnalyticsCards;
